import {
  getContractListData,
  getOpportunityOverviewContractListData,
} from '../contracts/mockData'
import { getOpportunityRegistrationListDataByOpportunityId } from '../opportunityRegistrations/mockData'
import { getOpportunityContactListDataByOpportunityId } from '../opportunityContacts/mockData'
import { getOpportunityTeamMemberListDataByOpportunityId } from '../opportunityTeamMembers/mockData'
import {
  getOpportunityMasterServiceDataById,
  getOpportunityMasterServiceListDataByOpportunityId,
} from '../opportunityMasterServices/mockData'
import { getOpportunityDocumentListDataByOpportunityId } from '../opportunityDocuments/mockData'
import { getOpportunityNoteListDataByOpportunityId } from '../opportunityNotes/mockData'
import { getTaskListDataByOpportunityId } from '../tasks/mockData'
import { getCallListDataByOpportunityId } from '../calls/mockData'
import { getEmailListDataByOpportunityId } from '../emails/mockData'
import { getMeetingListDataByOpportunityId } from '../meetings/mockData'
import { getOpportunityLastSelectedStateDataByOpportunityId } from '../opportunityLastSelectedStates/mockData'
import { getCompanyListDataByAccountOwnerId } from '../companies/mockData'
import { getOpportunityMSServiceListData } from '../opportunityMSServices/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import { todayPlusDaysISODateString } from '~/cynqueModules/utils/todayPlusDaysISODateString'
import { OpportunityMSServicesStatusIds } from '~/services/selectLists/opportunityMSServicesStatii'
import {
  getOpportunityStatusByLabel,
  getOpportunityStatusById,
  OpportunityStatusIds,
} from '~/services/selectLists/opportunityStatii'
import {
  getOpportunityTypeById,
  OpportunityTypeIds,
} from '~/services/selectLists'
import type { IContract } from '~/types/contract/models'
import type { ICompany } from '~/types/company/models'
import type {
  IOpportunity,
  IOpportunityMSService,
  IOpportunityOverview,
} from '~/types/opportunity/models'
import {
  type TEntityId,
  type ISelectItem,
  type IEntitySelectItem,
  type IEntitySelectItemWithOptionalAvatar,
  assertIsNotUndefined,
} from '~/types/models'
import { sumArray } from '~/cynqueModules/utils/simpleSumArray'
import { ContractTypeIds } from '~/services/selectLists/contractTypes'
import { ContractStatusIds } from '~/services/selectLists/contractStatii'

import { product, filter, map, uniqueByKey, chain } from '~/iterlib'

const mn = '[api/msw/api/modules/opportunities/mockdData.ts]'
const opportunityListData = undefined as unknown as IOpportunity[]

export const getOpportunityListData = getListDataFunctionFactory<
  IOpportunity[]
>({
  listData: opportunityListData,
  storageKey: 'opportunities',
  getStartingListData: getStartingOpportunityListData,
  forOfFunction: async (o) => {
    // const omsArray = await getOpportunityMasterServiceListDataByOpportunityId(
    //   o.id
    // )
    // console.debug('EXECUTING FOR EACH LOOP', o.id, o)
    const omsArray = await getOpportunityMasterServiceListDataByOpportunityId(
      o.id
    )
    // console.debug('EXECUTING AFTER AWAIT', o.id, omsArray)
    o.estimatedMrcAmount = sumArray(omsArray, 'estimatedMrcAmount')
    o.estimatedOneTimeAmount = sumArray(omsArray, 'estimatedOneTimeAmount')
    // console.debug(
    //   'EXECUTING AFTER SUMMING',
    //   o.id,
    //   o.estimatedMrcAmount,
    //   o.estimatedOneTimeAmount
    // )
  },
})

export function _getODataById(
  id: TEntityId,
  list: IOpportunity[]
): IOpportunity {
  if (!list) {
    throw new Error(`_getODataById called with undefined/null list`)
  }
  const opportunity = list.find((cct: IOpportunity) => cct.id === id)
  if (!opportunity) {
    throw new Error(`Can't find opportunity '${id}'`)
  }
  return opportunity
}

export function _getOSelectItemDataById(
  id: TEntityId,
  list: IOpportunity[]
): ISelectItem {
  if (!list) {
    throw new Error(
      `export async function _getOSelectItemDataById` +
        ` called with undefined/null list`
    )
  }
  const opportunity = _getODataById(id, list)
  if (!opportunity) {
    throw new Error('Opportunity mock data is falsey')
  }
  return { id: opportunity.id, label: opportunity.descr }
}

export async function getOpportunityDataById(
  id: string
): Promise<IOpportunity> {
  const list: IOpportunity[] = await getOpportunityListData()
  if (!list) {
    throw new Error(`opportunityList returned undefined/null`)
  }
  return _getODataById(id, list)
}

export async function getOpportunitySelectItemDataForId(id: string) {
  const list = await getOpportunityListData()
  return _getOSelectItemDataById(id, list)
}

export async function getOpportunityListDataByCompanyId(id: string) {
  const oListData = await getOpportunityListData()
  const coListData = oListData.filter((o: IOpportunity) => o.company.id === id)
  if (!coListData) {
    throw new Error('Company Opportunities mock data (coListData) is falsey')
  }
  return coListData
}

export async function getOpportunityListDataByCompanyAccountOwnerId(
  id: string
): Promise<IOpportunity[]> {
  const cListData = await getCompanyListDataByAccountOwnerId(id)
  const companyIdList = cListData.map((c) => c.id)
  console.debug('companyIdList:', companyIdList)
  const oListData = await getOpportunityListData()
  const oppList =
    oListData.filter((opp: IOpportunity) =>
      companyIdList.includes(opp.company.id as string)
    ) ?? []
  return oppList
}

export async function getOpportunityListDataByOwnerAndAssignedToId(
  id: string
): Promise<IOpportunity[]> {
  const oListData = await getOpportunityListData()
  const oppList =
    oListData.filter(
      (opp: IOpportunity) => opp.assignedTo?.id === id || opp.owner?.id === id
    ) ?? []
  return oppList
}

interface hasEntityId {
  id: TEntityId
}

const uniqueById = <T extends hasEntityId>(iterable: Iterable<T>) =>
  uniqueByKey(iterable, (obj) => obj.id)

export async function getOpportunityWithClosedWonServicesOnUnsignedContractsListData1(): Promise<
  IOpportunity[]
> {
  type IContractWithOpportunity = IContract & { opportunity: IEntitySelectItem }

  const draftContracts = filter(
    await getContractListData(),
    (contract): contract is IContractWithOpportunity =>
      !!contract.opportunity &&
      contract.contractType.id !== ContractTypeIds.MSA &&
      contract.status.id === ContractStatusIds.DRAFT
  )
  const conOmss = filter(
    product(draftContracts, await getOpportunityMSServiceListData()),
    (x): x is [IContractWithOpportunity, IOpportunityMSService] =>
      x[0].id === x[1].contract?.id &&
      x[1].status.id === OpportunityMSServicesStatusIds.CLOSED_WON
  )

  const opportunity_ids = new Set(
    map(conOmss, ([contract, _]) => contract.opportunity.id)
  )
  const opportunities = uniqueById(
    filter(
      await getOpportunityListData(),
      (opportunity): opportunity is IOpportunity =>
        opportunity_ids.has(opportunity.id)
    )
  )
  return Array.from(opportunities)
}

export async function getOpportunityWithClosedWonServicesOnUnsignedContractsListData(): Promise<
  IOpportunity[]
> {
  const fn = `${mn} getOpportunityWithClosedWonServicesOnUnsignedContractsListData |`
  console.debug(`${fn} running`)
  const allContractsList = await getContractListData()
  if (!allContractsList) {
    throw new Error(`getContractListData returned undefined/null`)
  }
  if (!allContractsList?.length) {
    console.debug(`${fn} no contracts found`)
    return []
  }
  const draftContractsList = allContractsList.filter(
    (contract: IContract) =>
      contract.opportunity?.id &&
      contract.contractType.id !== ContractTypeIds.MSA &&
      contract.status.id === ContractStatusIds.DRAFT
  )
  if (!draftContractsList.length) {
    console.debug(`${fn} no draft contracts found`)
    return []
  }
  console.debug(`${fn} draftContractsList:`, draftContractsList)
  const allOpportunityMSServicesList = await getOpportunityMSServiceListData()
  if (!allOpportunityMSServicesList) {
    throw new Error(`getContractListData returned undefined/null`)
  }
  const qualifyingOpportunityIdsSet: Set<TEntityId> = new Set()
  for (const contract of draftContractsList) {
    if (
      allOpportunityMSServicesList.some(
        (omss: IOpportunityMSService) =>
          omss.contract?.id === contract.id &&
          omss.status.id === OpportunityMSServicesStatusIds.CLOSED_WON
      )
    ) {
      assertIsNotUndefined(contract.opportunity?.id) // Should never be. Just satisfying Typesecript.
      qualifyingOpportunityIdsSet.add(contract.opportunity.id)
    }
  }
  console.debug(
    `${fn} qualifyingOpportunityIdsSet:`,
    qualifyingOpportunityIdsSet
  )
  const allOpportunitiesList = await getOpportunityListData()
  if (!allOpportunitiesList) {
    throw new Error(`getOpportunityListData returned undefined/null`)
  }
  const qualifyingOpportunitiesArray: IOpportunity[] =
    allOpportunitiesList.filter((opportunity) =>
      qualifyingOpportunityIdsSet.has(opportunity.id)
    )
  return qualifyingOpportunitiesArray ?? []
}

export async function getOpportunityOverviewData(
  id: string
): Promise<IOpportunityOverview | undefined> {
  const theOpportunityData = await getOpportunityDataById(id)
  if (!theOpportunityData) {
    throw new Error(`Opportunity data for ${id} not found`)
  }
  const theCompanyId = theOpportunityData.company.id as string

  const theOverviewData = {
    opportunity: theOpportunityData,
    opportunityTeamMembers:
      await getOpportunityTeamMemberListDataByOpportunityId(id),
    opportunityContacts: await getOpportunityContactListDataByOpportunityId(id),
    opportunityRegistrations:
      await getOpportunityRegistrationListDataByOpportunityId(id),
    opportunityDocuments: await getOpportunityDocumentListDataByOpportunityId(
      id
    ),
    opportunityNotes: await getOpportunityNoteListDataByOpportunityId(id),
    opportunityMasterServices:
      await getOpportunityMasterServiceListDataByOpportunityId(id),
    contracts: await getOpportunityOverviewContractListData({
      companyId: theOpportunityData?.company?.id,
      opportunityId: id,
    }),
    order: undefined,
    lastSelectedState: await getOpportunityLastSelectedStateDataByOpportunityId(
      id
    ),
    tasks: await getTaskListDataByOpportunityId(id),
    calls: await getCallListDataByOpportunityId(id),
    emails: await getEmailListDataByOpportunityId(id),
    meetings: await getMeetingListDataByOpportunityId(id),
    opportunities: await getOpportunityListDataByCompanyId(theCompanyId),
  }
  return theOverviewData
}

export async function getOpportunityDataByOMSId(
  id: string
): Promise<IOpportunity> {
  const opportunityMasterService = await getOpportunityMasterServiceDataById(id)
  if (!opportunityMasterService) {
    throw new Error(
      `getOpportunityMasterServiceDataById returned undefined/null for id ${id}`
    )
  }
  const list = await getOpportunityListData()
  // this test isn't needed, because getOpportunityListData should always return
  // a list.  And the test would also catch the case list == [], so the
  // error message isn't quite right.
  if (!list) {
    throw new Error(`getOpportunityListData returned undefined/null`)
  }
  const opportunity = list.find(
    (o: IOpportunity) => o.id === opportunityMasterService.opportunity.id
  )
  if (!opportunity) {
    throw new Error(
      `Can't find opportunity for id '${opportunityMasterService.opportunity.id}'`
    )
  }
  return opportunity
}

export async function getOpportunityMSServiceListDataByOpportunityId1(
  id: string
): Promise<IOpportunityMSService[]> {
  const opportunityMasterServices =
    await getOpportunityMasterServiceListDataByOpportunityId(id)
  const opportunityMSServices = chain<IOpportunityMSService>(
    ...map(opportunityMasterServices, (oms) => oms.opportunityMSServices || [])
  )
  return Array.from(opportunityMSServices)
}

export async function getOpportunityMSServiceListDataByOpportunityId(
  id: string
): Promise<IOpportunityMSService[]> {
  const fn = `${mn} getSupplierDataByOpportunityId |`
  const opportunityMasterServices =
    await getOpportunityMasterServiceListDataByOpportunityId(id)
  if (!opportunityMasterServices) {
    console.debug(
      'getOpportunityMasterServiceListDataByOpportunityId' +
        ' returned undefined/null. Returning emtpy array.'
    )
    return []
  }
  const opportunityMSServices = [] as unknown as IOpportunityMSService[]
  for (const oms of opportunityMasterServices) {
    opportunityMSServices.push(...oms.opportunityMSServices)
  }
  if (opportunityMSServices.length < 1) {
    console.debug(`${fn} opportunity has no omss'. Returning empty array.`)
  }
  return opportunityMSServices
}

export async function getSupplierListDataByOpportunityId1(
  id: string
): Promise<IEntitySelectItemWithOptionalAvatar[]> {
  type IOpportunityMSServiceWithSupplier = IOpportunityMSService & {
    supplier: IEntitySelectItemWithOptionalAvatar
  }

  const suppliers = uniqueById(
    map(
      filter(
        await getOpportunityMSServiceListDataByOpportunityId(id),
        (omss): omss is IOpportunityMSServiceWithSupplier =>
          !!omss.supplier && omss.doInclude
      ),
      (omss) => omss.supplier
    )
  )
  return Array.from(suppliers)
}

export async function getSupplierListDataByOpportunityId(
  id: string,
  onlyWithReadyForContractMSServices: boolean = false
): Promise<IEntitySelectItemWithOptionalAvatar[]> {
  const fn = `${mn} getSupplierDataByOpportunityId |`
  const opportunityMSServices =
    await getOpportunityMSServiceListDataByOpportunityId(id)
  if (opportunityMSServices.length < 1) {
    console.debug(
      `${fn} opportunity has no omss'. Can't get omss supplier listData'`
    )
    return []
  }
  return opportunityMSServices.reduce(
    (
      unique: IEntitySelectItemWithOptionalAvatar[],
      omss: IOpportunityMSService
    ) => {
      if (omss.supplier && omss.doInclude) {
        if (
          !onlyWithReadyForContractMSServices ||
          omss.status.id === OpportunityMSServicesStatusIds.READY_FOR_CONTRACT
        ) {
          if (!unique.some((obj) => obj.id === omss.supplier?.id)) {
            unique.push(omss.supplier)
          }
        }
      }
      return unique
    },
    []
  )
}

export async function updateCompanyInOpportunities({
  targetItem: company,
}: {
  targetItem: ICompany
}) {
  const fn = `${mn} updateCompanyInOpportunities |`
  const list: IOpportunity[] = await getOpportunityListData()
  if (!list) {
    throw new Error(`getOpportunityListData returned undefined/null`)
  }
  for (const opportunity of list) {
    if (opportunity.company.id === company.id) {
      console.debug(
        `${fn} updating companySelectItemInOpportunity from ${opportunity.company.label} to ${company.label}`
      )
      opportunity.company = { id: company.id, label: company.name }
    }
  }
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'opportunities',
    listData: list,
    callingFn: fn,
  })
}

export async function getStartingOpportunityListData(): Promise<
  IOpportunity[]
> {
  return [
    {
      id: 'XXO0001',
      hrid: 'O0001',
      company: {
        id: 'XXC0003',
        label: 'Boston Coach',
      },
      descr: 'UCaaS',
      suppliers: [],
      term: 36,
      anticipatedCloseDate: '',
      probability: 0,
      estimatedMrcAmount: 0,
      estimatedOneTimeAmount: 0,
      stage: { id: 'XXOS0001', label: 'Lead' },
      status: getOpportunityStatusById(OpportunityStatusIds.OPEN)!,
      opportunityType: [getOpportunityTypeById(OpportunityTypeIds.NEW)],
      defaultProject: undefined,
      recurs: 'M',
      paid: 'M',
      owner: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      assignedTo: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      numMasterServices: 0,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXO0002',
      hrid: 'O0002',
      company: {
        id: 'XXC0002',
        label: 'Far Reaching Health',
      },
      descr: 'UCaaS, Initial 1K seats',
      suppliers: [],
      term: 36,
      defaultTerm: 36,
      anticipatedCloseDate: todayPlusDaysISODateString(20),
      probability: 0.75,
      estimatedMrcAmount: 18000,
      estimatedOneTimeAmount: 1800,
      stage: { id: 'XXOS0008', label: 'Negotiation' },
      status: getOpportunityStatusById(OpportunityStatusIds.OPEN)!,
      opportunityType: [getOpportunityTypeById(OpportunityTypeIds.NEW)!],
      owner: {
        id: 'XXTM0003',
        label: 'Benedict Cumberbatch',
      },
      salesRep: {
        id: 'XXTM0003',
        label: 'Benedict Cumberbatch',
      },
      subAgent: {
        id: 'XXTM0005',
        label: 'Robert Downey, Jr.',
      },
      numMasterServices: 1,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXO0003',
      hrid: 'O0003',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      descr: 'DIA - Boston, Lake & Waltham',
      suppliers: [
        {
          id: 'XXS0002',
          label: 'Cogent',
        },
      ],
      term: 24,
      // The msServices under XXO0003 carry the term the resolver
      // (resolveMSServiceTerm) would give them today: 24 -- except the CLOSED
      // WON rows (Boston & Waltham Cogent, TAM), which keep 36 because they were
      // copied to ORD0002 at 36, and Lake's IP Block /29 (XXOMSS0088), which
      // keeps 36 because its supplier-product item stores 36. So Lake's Cogent
      // kit and the DIA service group both read "Varies".
      defaultTerm: 24,
      anticipatedCloseDate: todayPlusDaysISODateString(40),
      probability: 0.9,
      estimatedMrcAmount: 0, // 110000,
      estimatedOneTimeAmount: 0, // 5000,
      stage: { id: 'XXOS0009', label: 'Quotes Received' },
      status: getOpportunityStatusById(OpportunityStatusIds.SOME_CLOSED_WON)!,
      opportunityType: [getOpportunityTypeById(OpportunityTypeIds.NEW)!],
      defaultProject: {
        id: 'XXP0002',
        label: 'E-LAN to DIA Conversion',
      },
      owner: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      assignedTo: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      numMasterServices: 1,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXO0004',
      hrid: 'O0004',
      company: {
        id: 'XXC0002',
        label: 'Far Reaching Health',
      },
      descr: 'UCaaS, Addtl 500 seats',
      suppliers: [],
      term: 36,
      defaultTerm: 36,
      anticipatedCloseDate: todayPlusDaysISODateString(50),
      probability: 0.75,
      estimatedMrcAmount: 19000,
      estimatedOneTimeAmount: 1900,
      stage: { id: 'XXOS0008', label: 'Negotiation' },
      status: getOpportunityStatusById(OpportunityStatusIds.OPEN)!,
      opportunityType: [getOpportunityTypeById(OpportunityTypeIds.UPGRADE)!],
      owner: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      salesRep: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      subAgent: {
        id: 'XXTM0005',
        label: 'Robert Downey, Jr.',
      },
      numMasterServices: 1,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXO0005',
      hrid: 'O0005',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      descr: 'E-LAN - Initial Setup',
      suppliers: [],
      term: 36,
      defaultTerm: 36,
      anticipatedCloseDate: todayPlusDaysISODateString(70),
      probability: 0.95,
      estimatedMrcAmount: 19000,
      estimatedOneTimeAmount: 1900,
      stage: { id: 'XXOS0008', label: 'Negotiation' },
      status: getOpportunityStatusById(OpportunityStatusIds.OPEN)!,
      opportunityType: [getOpportunityTypeById(OpportunityTypeIds.NEW)!],
      owner: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      salesRep: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      subAgent: {
        id: 'XXTM0005',
        label: 'Robert Downey, Jr.',
      },
      numMasterServices: 1,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXO0006',
      hrid: 'O0006',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      descr: 'UCaaS - Initial Setup',
      suppliers: [],
      term: 36,
      defaultTerm: 36,
      anticipatedCloseDate: todayPlusDaysISODateString(100),
      probability: 0.85,
      estimatedMrcAmount: 21000,
      estimatedOneTimeAmount: 1600,
      stage: { id: 'XXOS0008', label: 'Negotiation' },
      status: getOpportunityStatusById(OpportunityStatusIds.OPEN)!,
      opportunityType: [getOpportunityTypeById(OpportunityTypeIds.NEW)!],
      owner: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      salesRep: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      subAgent: {
        id: 'XXTM0005',
        label: 'Robert Downey, Jr.',
      },
      numMasterServices: 1,
      version: '1',
      canBeDeleted: true,
    },
    {
      // Production's three Zscaler DLP year-kits, nested in one Kit. See
      // XXPR0074 and _buildZscalerDlpOpportunityMSServices.
      id: 'XXO0007',
      hrid: 'O0007',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      descr: 'Zscaler DLP - 3 Year',
      suppliers: [
        {
          id: 'XXS0011',
          label: 'AppDirect',
        },
      ],
      term: 36,
      defaultTerm: 36,
      anticipatedCloseDate: todayPlusDaysISODateString(30),
      probability: 0.9,
      estimatedMrcAmount: 0,
      estimatedOneTimeAmount: 0,
      stage: { id: 'XXOS0008', label: 'Negotiation' },
      status: getOpportunityStatusById(OpportunityStatusIds.OPEN)!,
      opportunityType: [getOpportunityTypeById(OpportunityTypeIds.NEW)!],
      owner: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      salesRep: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      numMasterServices: 1,
      version: '1',
      canBeDeleted: true,
    },
  ]
}
